// src/pages/Invitation.tsx
import React, { useState, useEffect } from "react";
import theme from "../theme";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import yellowUnderline from "../images/yellow_underline.png";
import { register } from "../services/auth";
import ProgressSteps from "../components/ProgressStep";
import api from "../services/api";
import { toast } from "react-toastify";
import { isValidPassword } from "../utils/inputValidation";

const PASSWORD_RULE_MESSAGE = "Password must be at least 8 characters and include a letter, a number, and a symbol (!@#$%^&*()_+-=)";

const Invitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  /* ───────────────────────────────
     Existing state (UNCHANGED names)
  ─────────────────────────────── */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [jobField, setJobField] = useState(""); // ← reused for Gender
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [invitationToken, setInvitationToken] = useState("");
  const [testName, setTestName] = useState("");
  const [industryName, setIndustryName] = useState("");
  const [testId, setTestId] = useState<number | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(true);

  // Helper function to get input style with error styling
  const getInputStyle = (fieldName: string): React.CSSProperties => {
    return {
      ...input,
      ...(errors[fieldName] ? inputError : {})
    };
  };

  // Helper function to get select style with error styling
  const getSelectStyle = (fieldName: string): React.CSSProperties => {
    return {
      ...select,
      ...(errors[fieldName] ? inputError : {})
    };
  };

  // Email validation helper
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const loadInvitationDetails = async (token: string) => {
    try {
      const response = await api.get(`invitation/detail/?token=${token}`);
      const data = response.data;

      // Pre-fill form with existing data
      const fullName = `${data.first_name} ${data.last_name}`.trim();
      const parts = fullName.split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
      setEmail(data.email || '');
      setPhoneNumber(data.phone || '');
      setJobField(data.gender || '');
      setDateOfBirth(data.date_of_birth || '');
      setTestName(data.test_name || '');
      setIndustryName(data.industry_name || '');
      setTestId(data.test_id || null);

      // If user already has an activated account, redirect to login
      if (data.requires_login) {
        toast.info('Please log in to access your assessment.');
        navigate(`/login?redirect=/begin-test&test_id=${data.test_id}`);
        return;
      }
    } catch (err: any) {
      console.error('Failed to load invitation:', err);
      toast.error('Invalid or expired invitation link');
      navigate('/login');
    } finally {
      setLoadingInvitation(false);
    }
  };

  // Load invitation details on component mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      toast.error('Invalid invitation link');
      navigate('/login');
      return;
    }

    setInvitationToken(token);
    loadInvitationDetails(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setErrors((prev) => {
      const errs = { ...prev };
      if (!isValidPassword(value))
        errs.password = PASSWORD_RULE_MESSAGE;
      else delete errs.password;
      if (repeatPassword && value !== repeatPassword)
        errs.repeatPassword = "Passwords do not match";
      else delete errs.repeatPassword;
      return errs;
    });
  };

  const handleRepeatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRepeatPassword(value);
    setErrors((prev) => {
      const errs = { ...prev };
      if (value !== password) errs.repeatPassword = "Passwords do not match";
      else delete errs.repeatPassword;
      return errs;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    // Comprehensive validation like register pages
    const newErrors: { [k: string]: string } = {};
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!jobField) newErrors.jobField = "Gender is required";
    if (!password) {
      newErrors.password = "Password is required";
    } else if (!isValidPassword(password)) {
      newErrors.password = PASSWORD_RULE_MESSAGE;
    }
    if (!repeatPassword) {
      newErrors.repeatPassword = "Please confirm your password";
    } else if (password && repeatPassword && password !== repeatPassword) {
      newErrors.repeatPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await api.post('invitation/accept/', {
        token: invitationToken,
        first_name: firstName,
        last_name: lastName,
        email,
        phone: phoneNumber,
        gender: jobField,
        date_of_birth: dateOfBirth || null,
        password,
      });

      // Backend no longer auto-logs the user in (#26). Send them through the
      // standard login flow; the redirect query string preserves the intent
      // to begin the just-activated test assignment.
      toast.success('Account activated. Please log in to begin your assessment.');
      const loginUrl = testId
        ? `/login?redirect=/begin-test&test_id=${testId}`
        : '/login?redirect=/begin-test';
      navigate(loginUrl);
    } catch (err: any) {
      console.error('Invitation acceptance failed:', err);
      const errorMessage = err.response?.data?.error || 'Failed to accept invitation. Please try again.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvitation) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <p>Loading invitation details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={container}>
      <div style={wrapper}>
        <ProgressSteps total={3} current={1} />
        <div style={card}>
          <h1 style={title}>You've been invited</h1>
          <img
            src={yellowUnderline}
            alt=""
            style={{ height: 10, float: "right", marginRight: "40px" }}
          />
          <p style={subtitle}>
            You have been invited to take the
            <br /> <strong>{testName}</strong> assessment for <strong>{industryName}</strong>.
            <br /> Before we begin, we just need a few details from you…
          </p>

          {apiError && <div style={errorStyle}>{apiError}</div>}

          <form style={form} onSubmit={handleSubmit}>

            {/* First Name */}
            <div>
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                style={getInputStyle('firstName')}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) {
                    setErrors(prev => ({ ...prev, firstName: '' }));
                  }
                }}
                required
              />
              {errors.firstName && (
                <div style={errorStyle}>{errors.firstName}</div>
              )}
            </div>

            {/* Last Name */}
            <div>
              <input
                type="text"
                placeholder="Last Name"
                value={lastName}
                style={getInputStyle('lastName')}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) {
                    setErrors(prev => ({ ...prev, lastName: '' }));
                  }
                }}
                required
              />
              {errors.lastName && (
                <div style={errorStyle}>{errors.lastName}</div>
              )}
            </div>

            {/* Phone number */}
            <div>
              <input
                type="tel"
                placeholder="Phone number"
                style={input}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </div>

            {/* Email — locked to the invitation address (#26). Editing it
                would let a token holder hijack the account, so the backend
                rejects mismatched emails. We disable the field client-side
                so the candidate sees the constraint upfront. */}
            <div>
              <input
                type="email"
                placeholder="jsmith@email.com"
                style={{ ...getInputStyle('email'), backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                value={email}
                readOnly
                disabled
                title="Email is locked to the address you were invited at"
              />
              {errors.email && <div style={errorStyle}>{errors.email}</div>}
            </div>

            {/* Gender (re-uses jobField state) */}
            <div style={selectBox}>
              <select
                value={jobField}
                onChange={(e) => {
                  setJobField(e.target.value);
                  if (errors.jobField) {
                    setErrors(prev => ({ ...prev, jobField: '' }));
                  }
                }}
                style={getSelectStyle('jobField')}
                required
              >
                <option value="" disabled>
                  Gender
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.jobField && (
                <div style={errorStyle}>{errors.jobField}</div>
              )}
            </div>

            {/* Date of birth */}
            <div style={{ position: "relative" }}>
              <label style={dateLabel}>Date of birth</label>
              <input
                type="date"
                style={{
                  ...input,
                  paddingLeft: "110px",
                  color: dateOfBirth ? "#495057" : "transparent"
                }}
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
              />
            </div>

            {/* Password */}
            <div style={passwordBox}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                style={getInputStyle('password')}
                value={password}
                onChange={(e) => {
                  handlePasswordChange(e);
                  if (errors.password) {
                    setErrors(prev => ({ ...prev, password: '' }));
                  }
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((x) => !x)}
                style={eyeBtn}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
              {errors.password && (
                <div style={errorStyle}>{errors.password}</div>
              )}
            </div>

            {/* Repeat password */}
            <div style={passwordBox}>
              <input
                type={showRepeat ? "text" : "password"}
                placeholder="Repeat password"
                style={getInputStyle('repeatPassword')}
                value={repeatPassword}
                onChange={(e) => {
                  handleRepeatChange(e);
                  if (errors.repeatPassword) {
                    setErrors(prev => ({ ...prev, repeatPassword: '' }));
                  }
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowRepeat((x) => !x)}
                style={eyeBtn}
              >
                {showRepeat ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </button>
              {errors.repeatPassword && (
                <div style={errorStyle}>{errors.repeatPassword}</div>
              )}
            </div>

            {/* Helper line */}
            <p style={helperTxt}>
              By creating a TestInvest account, you can log in to check results,
              download certificates, and more.
            </p>

            {/* Submit */}
            <button
              type="submit"
              style={submit}
              disabled={loading}
            >
              {loading ? "Loading…" : "Next"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────────────
   Styles
─────────────────────────────── */
const container: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
};
const card: React.CSSProperties = {
  width: "100%",
  maxWidth: 420,
  padding: "2.5rem 2rem 3rem",
  background: "#ffffff",
  textAlign: "center",
  borderRadius: 8,
};
const title: React.CSSProperties = {
  margin: 0,
  color: theme.colors.primary,
  fontSize: 32,
  fontWeight: 700,
};
const wrapper: React.CSSProperties = {
  width: "100%",
  maxWidth: 540,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1.75rem", // spacing between progress bar and card
};

const subtitle: React.CSSProperties = {
  color: "#000",
  fontSize: 14,
  lineHeight: 1.4,
  margin: "1.5rem 0 2rem",
};
const form: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};
const input: React.CSSProperties = {
  padding: "0.75rem 1rem",
  borderRadius: 9999,
  border: "none",
  background: "#eef2ff",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  fontSize: 14,
};

const inputError: React.CSSProperties = {
  background: "#FFEBEE",
  border: "2px solid #F44336",
  borderRadius: 9999,
};
const selectBox: React.CSSProperties = { position: "relative" };
const select: React.CSSProperties = {
  ...input,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  cursor: "pointer",
};
const passwordBox: React.CSSProperties = { position: "relative" };
const eyeBtn: React.CSSProperties = {
  position: "absolute",
  right: "1rem",
  top: "50%",
  transform: "translateY(-50%)",
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "#888",
  fontSize: "1rem",
};
const helperTxt: React.CSSProperties = {
  fontSize: 12,
  color: "#555",
  margin: "0.5rem 0 1.5rem",
};
const submit: React.CSSProperties = {
  padding: "0.9rem 1rem",
  border: "none",
  borderRadius: 9999,
  background: theme.colors.primary,
  color: "#fff",
  fontSize: "1rem",
  cursor: "pointer",
  fontWeight: 600,
};

const errorStyle: React.CSSProperties = {
  color: "red",
  fontSize: "0.875rem",
  marginTop: "0.25rem",
  textAlign: "left",
};

const dateLabel: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "1rem",
  transform: "translateY(-50%)",
  color: "#666",
  fontSize: "14px",
  pointerEvents: "none",
  zIndex: 1,
};

export default Invitation;
